import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CustomFieldInputs } from "@/components/CustomFieldInputs";
import { SystemBomEditor } from "@/components/SystemBomEditor";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCustomFieldDefinitions } from "@/hooks/useCustomFieldDefinitions";
import { useAuth } from "@/state/AuthContext";
import type { CustomFieldDefinition } from "@/types/customFields";
import { hasPermission } from "@/utils/permissions";
import { apiFetch } from "@/utils/api";

interface SystemRow {
  id: string;
  name: string;
  description: string;
  customFields?: Record<string, string>;
}

const emptyForm = { name: "", description: "" };

function initCustomFieldValues(
  definitions: CustomFieldDefinition[],
  existing?: Record<string, string>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const def of definitions) {
    values[def.fieldKey] =
      existing?.[def.fieldKey] ?? (def.fieldType === "boolean" ? "false" : "");
  }
  return values;
}

interface SystemFormFieldsProps {
  idPrefix: string;
  form: typeof emptyForm;
  onFormChange: (next: typeof emptyForm) => void;
  customFields: CustomFieldDefinition[];
  customFieldValues: Record<string, string>;
  onCustomFieldChange: (fieldKey: string, value: string) => void;
}

function SystemFormFields({
  idPrefix,
  form,
  onFormChange,
  customFields,
  customFieldValues,
  onCustomFieldChange,
}: SystemFormFieldsProps) {
  return (
    <>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-name`}>Name *</Label>
        <Input
          id={`${idPrefix}-name`}
          value={form.name}
          onChange={(e) => onFormChange({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-description`}>Description</Label>
        <Input
          id={`${idPrefix}-description`}
          value={form.description}
          onChange={(e) => onFormChange({ ...form, description: e.target.value })}
        />
      </div>
      <CustomFieldInputs
        definitions={customFields}
        values={customFieldValues}
        onChange={onCustomFieldChange}
        idPrefix={idPrefix}
      />
    </>
  );
}

export function AdminSystemsPage() {
  const { user } = useAuth();
  const canEditBom = hasPermission(user, "admin_bom_access");
  const [systems, setSystems] = useState<SystemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const { definitions: customFields, loading: fieldsLoading } = useCustomFieldDefinitions("system");

  const load = async () => {
    const data = await apiFetch<{ systems: SystemRow[] }>("/systems");
    setSystems(data.systems);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const resetCreateForm = () => {
    setForm(emptyForm);
    setCustomFieldValues(initCustomFieldValues(customFields));
    setFormError("");
  };

  const startEdit = (system: SystemRow) => {
    setShowNew(false);
    setEditingId(system.id);
    setFormError("");
    setForm({
      name: system.name,
      description: system.description ?? "",
    });
    setCustomFieldValues(initCustomFieldValues(customFields, system.customFields));
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetCreateForm();
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await apiFetch("/systems", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, customFields: customFieldValues }),
      });
      setShowNew(false);
      resetCreateForm();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create system");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setFormError("");
    setSaving(true);
    try {
      await apiFetch(`/systems/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, customFields: customFieldValues }),
      });
      cancelEdit();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update system");
    } finally {
      setSaving(false);
    }
  };

  if (loading || fieldsLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Systems"
        subtitle={
          <Link to="/admin/custom-fields?type=system" className="underline text-[#1e3a4f]">
            Manage custom fields
          </Link>
        }
        actions={
          <Button
            type="button"
            onClick={() => {
              if (showNew) {
                setShowNew(false);
                resetCreateForm();
              } else {
                setShowNew(true);
                setEditingId(null);
                resetCreateForm();
              }
            }}
            className="bg-[#1e3a4f] hover:bg-[#163041]"
          >
            {showNew ? "Cancel" : "Add System"}
          </Button>
        }
      />

      {showNew && (
        <form
          className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl"
          onSubmit={(e) => void create(e)}
        >
          <SystemFormFields
            idPrefix="new"
            form={form}
            onFormChange={setForm}
            customFields={customFields}
            customFieldValues={customFieldValues}
            onCustomFieldChange={(fieldKey, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
            }
          />
          {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
          <Button type="submit" disabled={saving} className="w-fit bg-[#1e3a4f] hover:bg-[#163041]">
            {saving ? "Creating…" : "Create system"}
          </Button>
        </form>
      )}

      {editingId && (
        <div className="bg-white rounded-lg shadow p-4 max-w-2xl border-2 border-[#1e3a4f]/20">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => void saveEdit(e)}>
            <p className="sm:col-span-2 text-sm font-semibold text-[#1e3a4f]">Edit system</p>
            <SystemFormFields
              idPrefix="edit"
              form={form}
              onFormChange={setForm}
              customFields={customFields}
              customFieldValues={customFieldValues}
              onCustomFieldChange={(fieldKey, value) =>
                setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
              }
            />
            {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving} className="bg-[#1e3a4f] hover:bg-[#163041]">
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button type="button" variant="secondary" disabled={saving} onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </form>
          {canEditBom && <SystemBomEditor systemId={editingId} />}
        </div>
      )}

      {!editingId && !showNew && (
      <DataTable
        columns={[
          { key: "name", header: "Name", render: (s) => s.name },
          { key: "desc", header: "Description", render: (s) => s.description || "—" },
          {
            key: "actions",
            header: "",
            render: (s) => (
              <Button
                size="sm"
                variant="secondary"
                disabled={editingId === s.id}
                onClick={() => startEdit(s)}
              >
                Edit
              </Button>
            ),
          },
        ]}
        rows={systems}
        rowKey={(s) => s.id}
      />
      )}
    </div>
  );
}
